// @ts-nocheck — verbatim port from xop-edge-staff. See src/crm/README.md.
import type { EmailRecipient } from '../email-target-selector';

/**
 * Pure recipient-building + email-uniqueness helpers for the email-campaign
 * target selector, extracted from `email-target-selector.tsx`. No money /
 * reseller here — wall-neutral. (`EmailRecipient` is imported type-only, so the
 * back-reference to the component erases at compile time — no runtime cycle.)
 */

/** Email-format check for a typed-in manual recipient. */
export const RECIPIENT_EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function isValidRecipientEmail(email: string): boolean {
  return RECIPIENT_EMAIL_RE.test(email);
}

/** A customer/prospect row — the fields the mapper reads (the real entity types
 * carry more; they satisfy this structurally). */
interface CompanyEntityLike {
  id: string | number;
  email?: string | null;
  full_name?: string | null;
  company_name?: string | null;
}

/** A directory-contact row. */
interface ContactLike {
  id: string | number;
  email?: string | null;
  full_name?: string | null;
  company?: string | null;
}

/** Shared core for the customer + prospect mappers (identical shape, differ only
 * in the recipient `type`). Keeps non-blank-email rows, names fall back to the
 * email, and `company` is omitted when blank (clean under exactOptional). */
function companyEntityRecipients(
  rows: CompanyEntityLike[],
  type: 'customer' | 'prospect',
): EmailRecipient[] {
  const out: EmailRecipient[] = [];
  for (const row of rows) {
    if (!row.email || row.email.trim().length === 0) continue;
    const email = row.email; // narrowed to a non-empty string; kept untrimmed (as before)
    out.push({
      id: String(row.id),
      name: row.full_name || email,
      email,
      type,
      ...(row.company_name ? { company: row.company_name } : {}),
    });
  }
  return out;
}

/** Customers with a non-blank email → `customer` recipient rows. */
export function customerRecipients(customers: CompanyEntityLike[]): EmailRecipient[] {
  return companyEntityRecipients(customers, 'customer');
}

/** Prospects (leads) with a non-blank email → `prospect` recipient rows. */
export function prospectRecipients(prospects: CompanyEntityLike[]): EmailRecipient[] {
  return companyEntityRecipients(prospects, 'prospect');
}

/** Directory contacts with a non-blank email → `contact` recipient rows. The id
 * is `contact_<id>` so it never collides with a customer/prospect of the same id. */
export function contactRecipients(contacts: ContactLike[]): EmailRecipient[] {
  const out: EmailRecipient[] = [];
  for (const c of contacts) {
    if (!c.email || c.email.trim().length === 0) continue;
    const email = c.email;
    out.push({
      id: `contact_${c.id}`,
      name: c.full_name || email,
      email,
      type: 'contact',
      ...(c.company ? { company: c.company } : {}),
    });
  }
  return out;
}

/** Case-insensitive: is `email` already among the selected recipients? */
export function isEmailSelected(recipients: EmailRecipient[], email: string): boolean {
  if (!email) return false;
  const lower = email.toLowerCase();
  return recipients.some((r) => !!r.email && r.email.toLowerCase() === lower);
}

/** Drop candidates whose email already appears in `selected` (case-insensitive). */
export function excludeSelectedByEmail(
  candidates: EmailRecipient[],
  selected: EmailRecipient[],
): EmailRecipient[] {
  return candidates.filter(
    (c) => !selected.some((r) => !!r.email && !!c.email && r.email.toLowerCase() === c.email.toLowerCase()),
  );
}
